import dayjs from "dayjs";
import {
  OnboardingLivePlanIssueCode,
  OnboardingLiveSlotPlanItemFragment,
} from "../gql/graphql.ts";

type PlanIssue = OnboardingLiveSlotPlanItemFragment["issues"][number];

export type PlanIssueDescription = {
  text: string;
  suggestion?: string;
};

export function describePlanIssue(
  issue: PlanIssue,
  {
    teacherNameOf,
    userGroupNameOf,
  }: {
    teacherNameOf: (teacherId?: string | null) => string;
    userGroupNameOf: (userGroupId: string) => string;
  },
): PlanIssueDescription {
  const teacher = teacherNameOf(issue.relatedTeacherId);
  const classRoom = issue.relatedClassRoom;
  const classRoomTime = classRoom
    ? dayjs(classRoom.startUtc).format("DD/MM HH:mm")
    : "";
  switch (issue.code) {
    case OnboardingLivePlanIssueCode.SlotInThePast:
      return {
        text: "Lo slot è nel passato o la live è già iniziata",
        suggestion: "Scegli un orario futuro",
      };
    case OnboardingLivePlanIssueCode.SlotNotOneHourLong:
      return { text: "La live deve durare esattamente un'ora" };
    case OnboardingLivePlanIssueCode.SlotOutsideUserGroupAvailability:
      return {
        text: "L'orario è fuori dalle fasce di prenotazione dello user group",
        suggestion:
          "Scegli un orario compreso nelle fasce del gruppo o modificale dalla pagina User Groups",
      };
    case OnboardingLivePlanIssueCode.UserGroupWithoutOnboardingLives:
      return {
        text: "Lo user group non prevede le live onboarding",
        suggestion: "Abilitale dalla pagina User Groups",
      };
    case OnboardingLivePlanIssueCode.MissingOnboardingLivePdf:
      return {
        text: "Manca il PDF onboarding dello user group",
        suggestion:
          "Aggiungilo nella sezione Onboarding Live PDFs di Course PDFs",
      };
    case OnboardingLivePlanIssueCode.SameUserGroupSameHour:
      return {
        text: classRoom
          ? `Esiste già una live dello stesso gruppo a quest'ora (${classRoomTime})`
          : "Un'altra live dello stesso gruppo di questa pianificazione è alla stessa ora",
        suggestion: "Scegli un altro orario",
      };
    case OnboardingLivePlanIssueCode.SameUserGroupSameDay:
      return {
        text: classRoom
          ? `Attenzione: c'è già un'altra live dello stesso gruppo in questo giorno (${classRoomTime})`
          : "Attenzione: questa pianificazione ha un'altra live dello stesso gruppo in questo giorno",
      };
    case OnboardingLivePlanIssueCode.OtherUserGroupSameDay:
      return {
        text: classRoom
          ? `In questo giorno c'è anche una live del gruppo ${userGroupNameOf(classRoom.userGroupId)} (${classRoomTime})`
          : "In questo giorno c'è anche una live di un altro gruppo",
      };
    case OnboardingLivePlanIssueCode.ConductorNotFound:
      return { text: "Conduttore non trovato" };
    case OnboardingLivePlanIssueCode.ConductorNotEnabledForOnboardingLives:
      return {
        text: `${teacher} non ha il tag Live Onboarding`,
        suggestion:
          "Aggiungi il tag dal suo profilo o scegli un altro conduttore",
      };
    case OnboardingLivePlanIssueCode.ConductorAlreadyPlannedAtSameTime:
      return {
        text: `${teacher} è già previsto per un'altra live di questa pianificazione alla stessa ora`,
        suggestion: "Scegli un altro conduttore o un altro orario",
      };
    case OnboardingLivePlanIssueCode.ConductorIsAlreadyTheCurrentOne:
      return { text: `${teacher} conduce già questa live` };
    case OnboardingLivePlanIssueCode.ConductorBusyWithOneOnOne:
      return {
        text: `${teacher} ha una lezione 1to1 alle ${classRoomTime} che non può essere riassegnata`,
        suggestion:
          "Scegli un altro conduttore o lascia l'assegnazione automatica",
      };
    case OnboardingLivePlanIssueCode.ConductorBusyWithOnboardingLive:
      return {
        text: `${teacher} conduce già un'altra live onboarding alle ${classRoomTime}`,
        suggestion: "Scegli un altro conduttore o un altro orario",
      };
    case OnboardingLivePlanIssueCode.ConductorBusyWithClassThatCannotBeReassigned:
      return {
        text: `${teacher} ha una classe alle ${classRoomTime} e nessun teacher disponibile e abilitato al gruppo può sostituirlo`,
        suggestion:
          "Chiedi a un teacher abilitato al gruppo di dare disponibilità, oppure scegli un altro conduttore",
      };
    case OnboardingLivePlanIssueCode.ConductorAlreadyRosteredOnShiftbase:
      return {
        text: `${teacher} ha già un turno su Shiftbase in questo orario`,
        suggestion:
          "Verifica il turno su Shiftbase o scegli un altro conduttore",
      };
    case OnboardingLivePlanIssueCode.NoConductorAvailable:
      return {
        text: "Nessun conduttore con tag Live Onboarding è disponibile a quest'ora",
        suggestion:
          "Chiedi ai conduttori di dare disponibilità oppure scegline uno specifico (il turno verrà creato forzatamente)",
      };
    case OnboardingLivePlanIssueCode.ForcedRoster:
      return {
        text: `${teacher} non ha dato disponibilità: il turno su Shiftbase verrà creato forzatamente`,
      };
    case OnboardingLivePlanIssueCode.ClassReassignment:
      return {
        text: classRoom
          ? `La classe ${classRoom.is1on1 ? "1to1" : "di gruppo"} delle ${classRoomTime} di ${teacherNameOf(classRoom.teacherId)} verrà riassegnata a ${teacher}`
          : `Una classe verrà riassegnata a ${teacher}`,
      };
  }
}
